<script setup lang="ts">
import { computed, ref, watch } from "vue";
import type { TaskDetail } from "@/types";
import { api } from "@/services/api";
import { useSessionStore } from "@/stores/session";
import UploadSourcePicker from "@/components/UploadSourcePicker.vue";

const props = defineProps<{ task: TaskDetail | null }>();
const emit = defineEmits<{ close: []; changed: [] }>();
const session = useSessionStore();
const message = ref("");
const deliveryName = ref("");
const deliverySummary = ref("");
const deliveryUrl = ref("");
const feedback = ref("");
const uploading = ref(false);
const isClient = computed(() => session.user?.role === "client");
const statusText = computed(
  () =>
    ({
      draft: "待确认",
      submitted: "已接收",
      scheduled: "已排期",
      in_progress: "制作中",
      blocked: "已阻塞",
      internal_review: "管理员审核中",
      ready_for_acceptance: "待客户验收",
      accepted: "已验收",
      revision_requested: "客户要求修改",
    })[props.task?.status || "draft"],
);
watch(
  () => props.task?.id,
  () => {
    message.value = "";
    deliveryName.value = "";
    deliverySummary.value = "";
    deliveryUrl.value = "";
    feedback.value = "";
  },
);
async function sendMessage() {
  if (!props.task || !message.value.trim()) return;
  await api(`/tasks/${props.task.id}/messages`, {
    method: "POST",
    body: JSON.stringify({ content: message.value }),
  });
  message.value = "";
  emit("changed");
}
async function submitDelivery() {
  if (!props.task) return;
  await api(`/tasks/${props.task.id}/deliveries`, {
    method: "POST",
    body: JSON.stringify({
      versionName: deliveryName.value,
      summary: deliverySummary.value,
      resultUrl: deliveryUrl.value,
    }),
  });
  emit("changed");
}
async function accept(action: "accepted" | "revision_requested") {
  if (!props.task) return;
  await api(`/tasks/${props.task.id}/acceptance`, {
    method: "POST",
    body: JSON.stringify({ action, feedback: feedback.value }),
  });
  emit("changed");
}
async function uploadAsset(event: Event) {
  const file = (event.target as HTMLInputElement).files?.[0];
  if (!props.task || !file) return;
  uploading.value = true;
  try {
    const form = new FormData();
    form.append("file", file);
    await api(`/tasks/${props.task.id}/assets`, { method: "POST", body: form });
    emit("changed");
  } finally {
    uploading.value = false;
    (event.target as HTMLInputElement).value = "";
  }
}
</script>

<template>
  <Teleport to="body"
    ><div v-if="task" class="backdrop" @click.self="emit('close')">
      <aside class="drawer">
        <header>
          <div>
            <h2>
              {{ isClient ? task.client_title || task.title : task.title }}
            </h2>
            <p class="muted">
              {{ statusText
              }}<span v-if="task.expected_at"> · {{ task.expected_at }}</span>
            </p>
          </div>
          <button class="btn" @click="emit('close')">×</button>
        </header>
        <main>
          <section>
            <h3>{{ task.brief_title }}</h3>
            <p>{{ isClient ? task.client_scope || task.scope : task.scope }}</p>
            <span
              v-for="item in isClient && task.client_deliverables?.length
                ? task.client_deliverables
                : task.deliverables"
              :key="item"
              class="chip"
              >{{ item }}</span
            >
          </section>
          <section>
            <h3>素材</h3>
            <UploadSourcePicker
              trigger-label="上传"
              :uploading="uploading"
              @media-change="uploadAsset"
              @file-change="uploadAsset"
            />
            <a
              v-for="asset in task.assets"
              :key="asset.id"
              class="asset"
              :href="asset.url"
              target="_blank"
              rel="noreferrer"
              >{{ asset.original_name
              }}<small
                >{{ asset.author_name }} ·
                {{ asset.created_at.slice(0, 16).replace("T", " ") }}</small
              ></a
            >
            <p v-if="!task.assets.length" class="muted">暂未上传素材</p>
          </section>
          <section>
            <h3>交付版本</h3>
            <article
              v-for="delivery in task.deliveries"
              :key="delivery.id"
              class="delivery"
            >
              <b>{{ delivery.version_name }}</b>
              <p>{{ delivery.summary }}</p>
              <a
                v-if="delivery.result_url"
                :href="delivery.result_url"
                target="_blank"
                rel="noreferrer"
                >打开交付链接</a
              ><small
                >{{ delivery.author_name }} ·
                {{ delivery.created_at.slice(0, 16).replace("T", " ") }}</small
              >
            </article>
            <p v-if="!task.deliveries.length" class="muted">
              团队尚未提交交付版本。
            </p>
          </section>
          <section>
            <h3>执行沟通</h3>
            <article
              v-for="item in task.messages"
              :key="item.id"
              class="message"
            >
              <small
                >{{ item.author_name || item.role }} ·
                {{ item.created_at.slice(0, 16).replace("T", " ") }}</small
              >
              <p>{{ item.content }}</p>
            </article>
            <textarea
              v-model="message"
              class="textarea"
              placeholder="补充要求、回复进度或提出修改意见…"
            /><button class="btn btn--blue" @click="sendMessage">
              发送留言
            </button>
          </section>
          <section v-if="isClient && task.status === 'ready_for_acceptance'">
            <h3>客户验收</h3>
            <textarea
              v-model="feedback"
              class="textarea"
              placeholder="可选：填写验收意见"
            />
            <div class="actions">
              <button class="btn btn--primary" @click="accept('accepted')">
                验收通过</button
              ><button
                class="btn btn--danger"
                @click="accept('revision_requested')"
              >
                要求修改
              </button>
            </div>
          </section>
          <section v-if="!isClient">
            <h3>提交交付版本</h3>
            <input
              v-model="deliveryName"
              class="input"
              placeholder="版本名称，例如 V1 脚本与分镜"
            /><textarea
              v-model="deliverySummary"
              class="textarea"
              placeholder="交付说明、已完成范围和需客户关注的内容"
            /><input
              v-model="deliveryUrl"
              class="input"
              placeholder="交付链接（可选）"
            /><button class="btn btn--primary" @click="submitDelivery">
              提交给客户验收
            </button>
          </section>
        </main>
      </aside>
    </div></Teleport
  >
</template>

<style scoped>
.chip {
  display: inline-block;
  margin: 3px 4px 0 0;
  padding: 3px 6px;
  background: #f2f5f5;
  color: var(--muted);
  font-size: 12px;
}
.asset {
  display: grid;
  gap: 3px;
  padding: 10px;
  border: 1px solid var(--line);
  background: #fafcfc;
  color: var(--ink);
  text-decoration: none;
}
.asset small {
  color: var(--muted);
}
.backdrop {
  position: fixed;
  inset: 0;
  z-index: 20;
  display: flex;
  justify-content: flex-end;
  background: rgba(20, 30, 40, 0.35);
}
.drawer {
  width: min(570px, 100%);
  height: 100%;
  background: #fff;
  box-shadow: -16px 0 42px rgba(23, 33, 43, 0.16);
  overflow: auto;
}
.drawer header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
  padding: 20px 24px;
  border-bottom: 1px solid var(--line);
}
.drawer h2,
.drawer h3 {
  margin: 0;
}
.drawer main {
  padding: 20px 24px;
}
.drawer section {
  display: grid;
  gap: 10px;
  padding: 0 0 20px;
  margin: 0 0 20px;
  border-bottom: 1px solid var(--line);
}
.delivery,
.message {
  padding: 12px;
  border: 1px solid var(--line);
  background: #fafcfc;
}
.delivery {
  border-left: 3px solid var(--success);
  background: #effaf7;
}
.delivery p,
.message p {
  margin: 5px 0;
}
.delivery small,
.message small {
  color: var(--muted);
}
.actions {
  display: flex;
  gap: 8px;
}
@media (max-width: 700px) {
  .drawer header,
  .drawer main {
    padding: 16px;
  }
}
</style>
